import React, { useCallback, useEffect, useState } from 'react';
import { CreditCard, Ticket, Percent } from 'lucide-react';
import { PaymentGatewayAdminResponse } from '../Types/PaymentGatewayType';
import { getPaymentGateways } from '../Services/PaymentGatewayAdminService';
import PaymentGatewayConfigComponent from '../Components/PaymentGatewayConfigComponent';
import TaxConfigComponent from '../Components/TaxConfigComponent';
import PaymentConfigByAdminPage from './PaymentConfigByAdminPage';

type Tab = 'GATEWAYS' | 'PLANS' | 'TAX';

/**
 * One screen for money, with the three tabs that only sit together because a checkout price, a
 * checkout option and the tax added to both are configured by the same person.
 *
 * <p>Kept as tabs rather than separate routes because the failure this screen exists to prevent is a
 * gateway enabled with no credentials against a plan that costs something, or a tax rate left on
 * while nobody realises every price has moved - an admin should be able to see all of it without
 * hunting through a menu.
 *
 * <p>TAB itself is deliberately not fetched here. {@link TaxConfigComponent} owns its own load, in
 * the same way the plan tab does, so mounting one does not require the other two to have resolved.
 */
function PaymentSystemConfigPage() {
  const [tab, setTab] = useState<Tab>('GATEWAYS');
  const [gateways, setGateways] = useState<PaymentGatewayAdminResponse[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchGateways = useCallback(async () => {
    try {
      const response = await getPaymentGateways();
      setGateways(response?.success && Array.isArray(response.data) ? response.data : []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGateways();
  }, [fetchGateways]);

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'GATEWAYS', label: 'Payment Gateways', icon: <CreditCard className="w-4 h-4" /> },
    { key: 'PLANS', label: 'Subscription Plans', icon: <Ticket className="w-4 h-4" /> },
    { key: 'TAX', label: 'Tax / GST', icon: <Percent className="w-4 h-4" /> },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-black text-white">Payment System Configuration</h1>
        <p className="mt-1 text-sm text-slate-400">
          Manage the payment providers customers can pay with, the plans they are buying, and the
          tax added to both.
        </p>
      </div>

      <div className="inline-flex rounded-2xl border border-slate-800 bg-slate-900/60 p-1">
        {tabs.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setTab(item.key)}
            className={`flex items-center gap-2 rounded-xl px-4 py-2 text-xs font-black uppercase tracking-widest transition ${
              tab === item.key
                ? 'bg-cyan-500/15 text-cyan-300'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            {item.icon}
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'GATEWAYS' &&
        (loading ? (
          <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-10 text-center text-sm text-slate-400">
            Loading gateways...
          </div>
        ) : (
          <PaymentGatewayConfigComponent data={gateways} onChanged={fetchGateways} />
        ))}

      {tab === 'PLANS' && <PaymentConfigByAdminPage />}

      {tab === 'TAX' && <TaxConfigComponent />}
    </div>
  );
}

export default PaymentSystemConfigPage;